import { useEffect, useRef } from 'react';

import {
  decideFind,
  decideProblem,
  decideStaffPartnerProduct,
  fetchStaffPartnerProducts,
  fetchStaffDocuments,
  fetchOwnerMoney,
  fetchStaffAnalytics,
  fetchStaffOverview,
  fetchStaffEnquiries,
  fetchStaffFeedback,
  fetchStaffFinds,
  fetchStaffProblems,
  fetchStaffRecoveries,
  fetchTeam,
  markEnquiryHandled,
  reviewDocument,
  writeOffRunner,
  type StaffArea,
  type StaffDocument,
  type StaffEnquiry,
  type StaffFindRequest,
  type StaffPartnerProduct,
  type StaffProblem,
  type StaffRecovery,
  fetchLearning,
  fetchBankPayments,
  fetchCancellations,
  fetchRefunds,
  fetchRunnersNow,
  fetchSignups,
  fetchStaffOrders,
  fetchStaffPartners,
  recordFelt,
} from '../lib/api';
import { allocateJobs, fetchAllocateBoard, runnerNamed } from '../lib/allocate-api';
import {
  fetchLockedOut,
  findToUnlock,
  locksNamed,
  resetShopperPin,
  unlockLocked,
} from '../lib/unlock-api';
import { money } from '../lib/money';
import { useOzi } from '../state/ozi';
import { fillerWhileWaiting } from '../voice/filler';
import { nameHeardIn } from '../voice/name';
import { knownSpeakerReply } from '@aldilivery/core';

import { KNOWN_NAMES } from '../lib/ozi-asker';
import {
  converse,
  CONVERSE_WAIT_MS,
  isKnownPhrase,
  OWNER_CONVERSE_WAIT_MS,
} from '../voice/phrases';
import {
  analyticsWords,
  cancellationsWords,
  moneyWords,
  periodRange,
  refundsWords,
  runnersNowWords,
  signupsWords,
  overviewWords,
  AREA_WORDS,
  help,
  readItem,
  spokenMoney,
  summary,
  understand,
  OWNER_ACKNOWLEDGEMENTS,
  orderNumbersMeant,
  ownerAnswersHere,
  partnerShopsWords,
  toOwner,
  waiting,
  type StaffCommand,
  type StaffLists,
} from '../voice/staff-voice';

const YES = /\b(yes|yeah|yep|correct|that'?s right|go ahead|confirm|do it|ok|okay)\b/i;
/** After Ozi answers, what is said next is taken as said to Ozi for this long, without its name. */
const CONVERSING_MS = 45_000;

const DECISION_WORDS = {
  no_fault: 'nobody was at fault',
  runner_at_fault: 'the Runner was responsible',
  shop_at_fault: 'the shop was responsible',
  platform_at_fault: 'we were responsible',
  shopper_at_fault: 'the Shopper was responsible, so no refund',
} as const;

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

/** "31st of March 2027" or "31/3/2027" as 2027-03-31; null when it is not a date. */
export function spokenDate(said: string): string | null {
  const text = said.toLowerCase();
  const numeric = text.match(/(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
  let day: number | undefined;
  let month: number | undefined;
  let year: number | undefined;
  if (numeric) {
    day = Number(numeric[1]);
    month = Number(numeric[2]);
    year = Number(numeric[3]);
  } else {
    const index = MONTHS.findIndex((name) => text.includes(name));
    const dayMatch = text.match(/\b(\d{1,2})(st|nd|rd|th)?\b/);
    const yearMatch = text.match(/\b(20\d{2})\b/);
    if (index >= 0 && dayMatch && yearMatch) {
      day = Number(dayMatch[1]);
      month = index + 1;
      year = Number(yearMatch[1]);
    }
  }
  if (!day || !month || !year || month > 12 || day > 31) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Ozi in the admin panel (7 October 2026): says what is waiting when someone signs in, reads
 * every list aloud, moves between the tabs, and makes decisions by voice, each read back and
 * done only after a yes. So the whole panel can be used without touching or seeing it.
 */
export function StaffVoice({
  staffKey,
  by,
  name,
  title,
  areas,
  address = null,
  canAllocate = false,
  canUnlock = false,
  onOpen,
  onChanged,
  onSignOut,
}: {
  staffKey: string;
  by: string;
  name: string;
  title: string;
  areas: readonly StaffArea[];
  /** The owner only: how he is addressed, "Mr Anthony", and every reply ends with sir. */
  address?: string | null;
  /** May send orders straight to a chosen Runner (ruling 80). */
  canAllocate?: boolean;
  /** May unlock people, and reset a PIN or a password (ruling 81). */
  canUnlock?: boolean;
  /** Show this tab on the screen too. */
  onOpen: (area: StaffArea) => void;
  /** Something was decided: the screen loads its lists again. */
  onChanged: (news: string) => void;
  onSignOut: () => void;
}): null {
  const ozi = useOzi();
  const lists = useRef<StaffLists>({});
  const cursor = useRef<{ area: StaffArea; index: number } | null>(null);
  const latest = useRef({
    ozi,
    staffKey,
    by,
    name,
    title,
    areas,
    address,
    canAllocate,
    canUnlock,
    onOpen,
    onChanged,
    onSignOut,
  });
  latest.current = {
    ozi,
    staffKey,
    by,
    name,
    title,
    areas,
    address,
    canAllocate,
    canUnlock,
    onOpen,
    onChanged,
    onSignOut,
  };
  const turn = useRef(0);
  const acknowledge = useRef(false);
  const conversingUntil = useRef(0);

  useEffect(() => {
    const { ozi: voice } = latest.current;
    let cancelled = false;

    async function load(): Promise<void> {
      const { staffKey: key, areas: mine } = latest.current;
      const next: StaffLists = {};
      const loaders: Array<Promise<void>> = [];
      const add = <T,>(
        area: StaffArea,
        load: () => Promise<T>,
        pick: (result: T) => unknown[],
      ): void => {
        if (!mine.includes(area)) return;
        loaders.push(
          load()
            .then((result) => {
              (next as Record<string, unknown>)[area] = pick(result);
            })
            .catch(() => undefined),
        );
      };
      add(
        'documents',
        () => fetchStaffDocuments(key),
        (r) => r.documents,
      );
      add(
        'problems',
        () => fetchStaffProblems(key),
        (r) => r.reports,
      );
      add(
        'feedback',
        () => fetchStaffFeedback(key),
        (r) => r.feedback,
      );
      add(
        'owed',
        () => fetchStaffRecoveries(key),
        (r) => r.recoveries,
      );
      add(
        'finds',
        () => fetchStaffFinds(key),
        (r) => r.requests,
      );
      add(
        'enquiries',
        () => fetchStaffEnquiries(key),
        (r) => r.enquiries,
      );
      add(
        'partners',
        () => fetchStaffPartnerProducts(key),
        (r) => r.products,
      );
      add(
        'learning',
        () => fetchLearning(key),
        (r) => r.waiting,
      );
      add(
        'payments',
        () => fetchBankPayments(key),
        (r) => r.waiting,
      );
      add(
        'team',
        () => fetchTeam(key),
        (r) => r.team,
      );
      add(
        'orders',
        () => fetchStaffOrders(key, { view: 'live' }),
        (r) => r.orders,
      );
      // What needs a person now (16 October 2026 review), said first when it is there.
      add(
        'overview',
        () => fetchStaffOverview(key),
        (r) => r.needsYou ?? [],
      );
      await Promise.all(loaders);
      lists.current = next;
    }

    /** For the owner, "sir" at the end, and "Yes, sir." first when he asked for something. */
    function words(text: string): string {
      if (!latest.current.address) return text;
      const first = acknowledge.current
        ? OWNER_ACKNOWLEDGEMENTS[turn.current++ % OWNER_ACKNOWLEDGEMENTS.length]!
        : null;
      acknowledge.current = false;
      return toOwner(text, first);
    }

    function say(text: string): void {
      void latest.current.ozi.say(words(text));
    }

    /** The opening words, by the owner's form of address when it is him. */
    function opening(): string {
      const { address: owner, name: who, title: job, areas: mine } = latest.current;
      return summary(owner ?? who, job, mine, lists.current);
    }

    function current(): unknown {
      const at = cursor.current;
      return at ? waiting(lists.current, at.area)[at.index] : undefined;
    }

    function readAt(area: StaffArea, index: number): void {
      const items = waiting(lists.current, area);
      if (items.length === 0) {
        cursor.current = { area, index: 0 };
        say(`There are no ${AREA_WORDS[area].many} right now.`);
        return;
      }
      const bounded = Math.max(0, Math.min(index, items.length - 1));
      cursor.current = { area, index: bounded };
      const end = index >= items.length ? 'That was the last one. ' : '';
      say(end + readItem(area, items[bounded], `${bounded + 1} of ${items.length}`));
    }

    /** Reads a decision back, and does it only after a yes. */
    function confirm(question: string, run: () => Promise<string>): void {
      latest.current.ozi.listenFor(words(`${question} Say yes to confirm.`), (answer) => {
        if (!YES.test(answer)) {
          say('All right, nothing was done.');
          return;
        }
        run()
          .then(async (done) => {
            await load();
            latest.current.onChanged(done);
            const area = cursor.current?.area;
            const left = area ? waiting(lists.current, area).length : 0;
            say(
              `${done} ` +
                (area && left > 0
                  ? `There ${left === 1 ? 'is' : 'are'} ${left} more. Say "next" to hear ${left === 1 ? 'it' : 'the next'}.`
                  : 'That is everything here.'),
            );
            if (area) cursor.current = { area, index: 0 };
          })
          .catch((failure: unknown) =>
            say(
              failure instanceof Error
                ? failure.message
                : 'That did not work. Nothing was changed.',
            ),
          );
      });
    }

    /**
     * One turn of a conversation with the server (31 October 2026). When it reads a decision
     * back (the owner's desk, Rule One), his very next words go straight back to it, so only
     * that answer can be the yes.
     */
    function talk(text: string, fallback?: () => void): void {
      const { staffKey: key, areas: mine, address: owner } = latest.current;
      const as = { kind: 'staff', key } as const;
      const thisTurn = turn.current;
      // The reply is spoken a sentence at a time as it arrives (ruling 92), queued so the
      // sentences run on without a click. The owner's model may think for a while (ruling 86):
      // only after `ozi.fillerAfterMs` of silence does he hear a short filler, never the same one
      // twice running, and "Looking that up." instead when his model is reading his desk. The
      // delay he felt, to the first word, is sent back to be measured.
      const heardAt = Date.now();
      let firstWordAt: number | null = null;
      const firstWord = (): void => {
        if (firstWordAt === null) firstWordAt = Date.now();
      };
      const filler = fillerWhileWaiting((words) => void latest.current.ozi.say(words));
      void (async () => {
        const answer = await latest.current.ozi.think(
          converse(text, thisTurn, as, owner ? OWNER_CONVERSE_WAIT_MS : CONVERSE_WAIT_MS, {
            onSentence: (sentence) => {
              filler.arrived();
              firstWord();
              void latest.current.ozi.say(sentence, { queue: true });
            },
            onStatus: (status) => {
              filler.arrived();
              void latest.current.ozi.say(status);
            },
          }),
        );
        filler.stop();
        turn.current += 1;
        if (!answer?.reply && fallback) {
          // Away, slow or silent: the panel's own answer, which names the right thing.
          fallback();
          return;
        }
        if (!answer?.reply) {
          // Slow or away: still personal (ruling 84), "Good morning, Mr. Anthony", from config.
          const local = knownSpeakerReply(text, whoIsHere(), KNOWN_NAMES, new Date());
          if (local) {
            void latest.current.ozi.say(local);
            return;
          }
          say(`Sorry, I didn't catch that. ${help(mine)}`);
          return;
        }
        conversingUntil.current = Date.now() + CONVERSING_MS;
        if (answer.awaitingYes) {
          firstWord();
          recordFelt(thisTurn, firstWordAt! - heardAt, as);
          latest.current.ozi.listenFor(answer.unsaid || answer.reply, (reply) => {
            talk(reply);
            if (YES.test(reply)) void load();
          });
          return;
        }
        if (answer.unsaid) {
          firstWord();
          void latest.current.ozi.say(answer.unsaid, { queue: true });
        }
        if (firstWordAt !== null) recordFelt(thisTurn, firstWordAt - heardAt, as);
      })();
    }

    /** Who is signed in here, for Ozi's own words when the server is slow (ruling 84). */
    function whoIsHere(): { kind: 'owner' | 'staff'; firstName: string | null } {
      const { address: owner, name: who } = latest.current;
      return owner
        ? { kind: 'owner', firstName: null }
        : { kind: 'staff', firstName: who.trim().split(/\s+/)[0] || null };
    }

    function needs(area: StaffArea): boolean {
      if (cursor.current?.area === area && current()) return true;
      say(
        `First, say "read me the ${AREA_WORDS[area].tab.toLowerCase()}", and I'll read them one at a time.`,
      );
      return false;
    }

    /** Whether an item of a list is being read out now, for "next", "accept" and the like. */
    function readingNow(): boolean {
      return cursor.current !== null && current() !== undefined;
    }

    function handle(text: string): boolean {
      const command = understand(text);
      // The owner (ruling 86): everything he says goes to his conversation first, questions,
      // requests and statements alike; the panel answers by itself only for explicit moves,
      // signing out and the item being read to him, and otherwise only when the conversation is
      // away, slow or silent.
      if (latest.current.address && !ownerAnswersHere(command, text, readingNow())) {
        talk(text, command ? () => run(command, true) : undefined);
        return true;
      }
      if (!command) {
        const { staffKey: key } = latest.current;
        // Nothing said in the admin panel is ever taken as a shopping order. Anything said to
        // Ozi by name, soon after Ozi answered, a greeting, or one of this account's everyday
        // phrases is a real conversation (31 October 2026, ruling 84): the server knows who is
        // speaking, "hello" included, and for the owner looks at his desk; "One moment" shows
        // meanwhile. Anything else is the room, not Ozi, and is left alone.
        void (async () => {
          const meant =
            nameHeardIn(text) ||
            Date.now() < conversingUntil.current ||
            knownSpeakerReply(text, whoIsHere(), KNOWN_NAMES, new Date()) !== null ||
            (await isKnownPhrase(text, { kind: 'staff', key }));
          if (meant) talk(text);
        })();
        return true;
      }
      return run(command, false);
    }

    /** Carries out what was understood. `fallback`: the owner's conversation did not answer. */
    function run(command: StaffCommand, fallback: boolean): boolean {
      const { staffKey: key, by: who, areas: mine } = latest.current;
      acknowledge.current = command.kind !== 'help' && command.kind !== 'summary';
      switch (command.kind) {
        case 'help':
          say(help(mine, latest.current.canUnlock));
          return true;
        case 'summary':
          void load().then(() => say(opening()));
          return true;
        case 'sign-out':
          say('Signing you out. Goodbye.');
          latest.current.onSignOut();
          return true;
        case 'open':
          if (!mine.includes(command.area)) {
            say(`${AREA_WORDS[command.area].tab} isn't part of your job, so I can't open it.`);
            return true;
          }
          latest.current.onOpen(command.area);
          if (command.area === 'overview') {
            void fetchStaffOverview(key)
              .then((data) => say(overviewWords(data)))
              .catch(() => say('The overview could not be loaded just now.'));
            return true;
          }
          if (command.area === 'money') {
            void fetchOwnerMoney(key)
              .then((data) => say(moneyWords(data)))
              .catch(() => say('The money could not be loaded just now.'));
            return true;
          }
          if (command.area === 'runners') {
            void fetchRunnersNow(key)
              .then((data) => say(runnersNowWords(data)))
              .catch(() => say('The Runners could not be loaded just now.'));
            return true;
          }
          if (command.area === 'reports') {
            say(
              'The reports are on the screen. Ask, for example, "how many signups this month", "what refunds went out yesterday and why", or "read me the cancellations".',
            );
            return true;
          }
          if (command.area === 'accounts' || command.area === 'audit') {
            // Names stay on the screen, never read aloud (Section Q).
            say(
              `${AREA_WORDS[command.area].tab} is open on the screen. I don't read names out loud, for privacy.`,
            );
            return true;
          }
          if (command.area === 'analytics') {
            void fetchStaffAnalytics(key, 'month')
              .then((data) => say(analyticsWords(data)))
              .catch(() => say('The numbers could not be loaded just now.'));
            return true;
          }
          void load().then(() => readAt(command.area, 0));
          return true;
        case 'next':
        case 'previous':
        case 'again': {
          const at = cursor.current;
          if (!at) {
            say('Say "read me the" and a list first, such as the complaints.');
            return true;
          }
          readAt(
            at.area,
            at.index + (command.kind === 'next' ? 1 : command.kind === 'previous' ? -1 : 0),
          );
          return true;
        }
        case 'decide': {
          if (!needs('problems')) return true;
          const report = current() as StaffProblem;
          const decide = (pence: number): void =>
            confirm(
              `Decide that ${DECISION_WORDS[command.decision]}${pence > 0 ? `, and refund ${money(pence)}` : ', with no refund'}?`,
              () =>
                decideProblem(key, report.id, {
                  decision: command.decision,
                  refundPence: pence,
                  note: 'Decided by voice.',
                  by: who,
                }).then(() => 'Decided.'),
            );
          if (command.refundPence !== null) {
            decide(command.refundPence);
          } else {
            latest.current.ozi.listenFor(
              'How much should be refunded? Say an amount, or none.',
              (answer) => {
                const pence = /\b(none|nothing|no refund|zero)\b/i.test(answer)
                  ? 0
                  : spokenMoney(answer);
                if (pence === null) say("I didn't catch an amount, so nothing was done.");
                else decide(pence);
              },
            );
          }
          return true;
        }
        case 'accept':
        case 'reject': {
          if (cursor.current?.area === 'partners' && current()) {
            const product = current() as StaffPartnerProduct;
            const approve = command.kind === 'accept';
            confirm(
              `${approve ? 'Accept' : 'Turn down'} ${product.name} from ${product.shopName}${approve ? ', so Shoppers can see it' : ''}?`,
              () =>
                decideStaffPartnerProduct(key, product.id, approve).then(() =>
                  approve ? 'Accepted. It is live now.' : 'Turned down.',
                ),
            );
            return true;
          }
          // The owner, with nothing being read to him: "approve the new shop" is about his desk,
          // which only his conversation reads back and decides; it did not answer this time.
          if (latest.current.address && !(cursor.current?.area === 'documents' && current())) {
            say(
              fallback
                ? "I can't reach your desk just now, so nothing was done. Please ask me again in a moment."
                : 'First, say "read me the documents", and I\'ll read them one at a time.',
            );
            return true;
          }
          if (!needs('documents')) return true;
          const doc = current() as StaffDocument;
          const decision = command.kind;
          const send = (expiresOn?: string): void =>
            confirm(
              `${decision === 'accept' ? 'Accept' : 'Turn down'} ${doc.runner?.name ?? 'this Runner'}'s ${doc.name}${expiresOn ? `, ending ${new Date(expiresOn).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}?`,
              () =>
                reviewDocument(key, doc.id, {
                  decision,
                  by: who,
                  ...(expiresOn ? { expiresOn } : {}),
                }).then(() => (decision === 'accept' ? 'Accepted.' : 'Turned down.')),
            );
          if (decision === 'accept' && doc.kind === 'insurance') {
            latest.current.ozi.listenFor(
              'When does the insurance end? Say the date, such as 31st March 2027.',
              (answer) => {
                const date = spokenDate(answer);
                if (!date) say("I didn't catch a date, so nothing was done.");
                else send(date);
              },
            );
          } else {
            send();
          }
          return true;
        }
        case 'write-off': {
          if (!needs('owed')) return true;
          const row = current() as StaffRecovery;
          if (!row.canWriteOff) {
            say(
              `${money(row.remainingPence)} is too much to write off. Please ask ${row.runner.name} for it.`,
            );
            return true;
          }
          confirm(`Write off the ${money(row.remainingPence)} ${row.runner.name} owes?`, () =>
            writeOffRunner(key, row.runner.id, who).then((result) => result.message),
          );
          return true;
        }
        case 'not-found': {
          if (!needs('finds')) return true;
          const row = current() as StaffFindRequest;
          confirm(
            `Tell ${row.shopperName} that ${row.description} could not be found${row.feePence > 0 ? `, and give back the ${money(row.feePence)}` : ''}?`,
            () =>
              decideFind(key, row.id, { found: false, note: 'None of the shops had it.' }).then(
                () => 'Done.',
              ),
          );
          return true;
        }
        case 'found': {
          if (!needs('finds')) return true;
          const row = current() as StaffFindRequest;
          const voice = latest.current.ozi;
          voice.listenFor(
            'What did you find? Say it as it should appear, such as Welsh cakes, 6 pack.',
            (found) => {
              voice.listenFor('Which shop?', (shop) => {
                voice.listenFor('How much was it?', (price) => {
                  const pence = spokenMoney(price);
                  if (pence === null || pence <= 0) {
                    say("I didn't catch a price, so nothing was done.");
                    return;
                  }
                  confirm(
                    `Found: ${found}, at ${shop}, for ${money(pence)}, for ${row.shopperName}?`,
                    () =>
                      decideFind(key, row.id, {
                        found: true,
                        name: found.trim(),
                        shop: shop.trim(),
                        pricePence: pence,
                      }).then(() => `Done. ${row.shopperName} can now add it to their basket.`),
                  );
                });
              });
            },
          );
          return true;
        }
        case 'signups':
        case 'refunds':
        case 'cancellations': {
          // Numbers, totals and patterns only, never a name (Section Q): the owner may be
          // asking in a public place.
          if (!mine.includes('reports')) {
            say("The reports aren't part of your job, so I can't answer that.");
            return true;
          }
          const range = periodRange(command.period);
          const between = { from: range.from.toISOString(), to: range.to.toISOString() };
          const answer =
            command.kind === 'signups'
              ? fetchSignups(key, 'month', between).then((data) =>
                  signupsWords(
                    range.words,
                    data.between?.shoppers ?? 0,
                    data.between?.runners ?? 0,
                  ),
                )
              : command.kind === 'refunds'
                ? fetchRefunds(key, between).then((data) => refundsWords(range.words, data))
                : fetchCancellations(key, between).then((data) =>
                    cancellationsWords(range.words, data),
                  );
          void answer
            .then((text) => say(text))
            .catch(() => say("I couldn't load that just now, so I don't know yet."));
          return true;
        }
        case 'runners-now': {
          if (!mine.includes('runners')) {
            say("The Runners list isn't part of your job, so I can't answer that.");
            return true;
          }
          latest.current.onOpen('runners');
          void fetchRunnersNow(key)
            .then((data) => say(runnersNowWords(data)))
            .catch(() => say("I couldn't load that just now, so I don't know yet."));
          return true;
        }
        case 'allocate': {
          // Sending jobs straight to a Runner (ruling 80): read back, sent only after a yes, and
          // the results said aloud, the ones that could not go with why.
          if (!mine.includes('orders') || !latest.current.canAllocate) {
            say(
              'Sending jobs straight to a Runner is for the owner, and the staff he lets allocate.',
            );
            return true;
          }
          latest.current.onOpen('orders');
          void fetchAllocateBoard(key)
            .then((board) => {
              const runner = runnerNamed(command.runner, board.runners);
              if (!runner) {
                say(`I couldn't find a Runner called ${command.runner}, so nothing was sent.`);
                return;
              }
              const chosen =
                command.orders === 'all'
                  ? board.orders.filter(
                      (order) =>
                        order.sentTo === null && order.status !== 'accepted' && !order.bookedLater,
                    )
                  : orderNumbersMeant(command.orders, board.orders.length)
                      .map((number) => board.orders.find((order) => order.number === number))
                      .filter((order): order is (typeof board.orders)[number] => !!order);
              if (chosen.length === 0) {
                say(
                  command.orders === 'all'
                    ? 'No orders are waiting for a Runner, so nothing was sent.'
                    : `I couldn't find those order numbers. There ${board.orders.length === 1 ? 'is 1 order' : `are ${board.orders.length} orders`} on the list.`,
                );
                return;
              }
              const which =
                chosen.length === 1
                  ? `order ${chosen[0]!.number}, ${chosen[0]!.reference}`
                  : `${chosen.length} orders, numbers ${chosen.map((order) => order.number).join(', ')}`;
              latest.current.ozi.listenFor(
                words(`Send ${which}, to ${runner.name}? Say yes to confirm.`),
                (answer) => {
                  if (!YES.test(answer)) {
                    say('All right, nothing was sent.');
                    return;
                  }
                  allocateJobs(
                    key,
                    chosen.map((order) => ({ orderId: order.id, runnerId: runner.id })),
                  )
                    .then((result) => {
                      latest.current.onChanged(result.message);
                      say(result.message);
                    })
                    .catch((failure: unknown) =>
                      say(
                        failure instanceof Error
                          ? failure.message
                          : 'That did not work. Nothing was sent.',
                      ),
                    );
                },
              );
            })
            .catch((failure: unknown) =>
              say(
                failure instanceof Error
                  ? failure.message
                  : 'The orders could not be loaded just now.',
              ),
            );
          return true;
        }
        case 'unlock':
        case 'reset-pin': {
          // Unlocking someone, or resetting a Shopper's PIN (ruling 81): read back, done only
          // after a yes, and the result said aloud. Never a PIN, a password or a code.
          if (!latest.current.canUnlock) {
            say('Unlocking people is for the owner, and the staff he lets unlock.');
            return true;
          }
          const confirmThen = (question: string, act: () => Promise<string>): void => {
            latest.current.ozi.listenFor(words(`${question} Say yes to confirm.`), (answer) => {
              if (!YES.test(answer)) {
                say('All right, nothing was changed.');
                return;
              }
              act()
                .then((message) => {
                  latest.current.onChanged(message);
                  say(message);
                })
                .catch((failure: unknown) =>
                  say(failure instanceof Error ? failure.message : 'That did not work.'),
                );
            });
          };
          if (command.kind === 'unlock') {
            void fetchLockedOut(key)
              .then(({ lockedOut }) => {
                const locks = locksNamed(command.name, lockedOut);
                if (locks.length === 0) {
                  say(`Nobody called ${command.name} is locked out just now.`);
                  return;
                }
                confirmThen(
                  `Unlock ${locks.map((lock) => lock.words).join('; and ')}?`,
                  async () => {
                    const said: string[] = [];
                    for (const lock of locks) said.push((await unlockLocked(key, lock)).message);
                    return said.join(' ');
                  },
                );
              })
              .catch((failure: unknown) =>
                say(failure instanceof Error ? failure.message : 'I could not look just now.'),
              );
            return true;
          }
          void findToUnlock(key, command.name)
            .then(({ people }) => {
              const shoppers = people.filter((person) => person.account === 'shopper');
              if (shoppers.length === 0) {
                say(`I couldn't find a Shopper called ${command.name}, so nothing was reset.`);
                return;
              }
              if (shoppers.length > 1) {
                say(
                  `I found ${shoppers.length} Shoppers called ${command.name}, so nothing was reset. Please choose the right one on Needs you now, under Find someone to unlock or reset.`,
                );
                latest.current.onOpen('overview');
                return;
              }
              const shopper = shoppers[0]!;
              confirmThen(
                `Reset ${shopper.name}'s PIN? They will choose a new one the next time it is needed, and we text them to say so.`,
                async () => (await resetShopperPin(key, shopper.id)).message,
              );
            })
            .catch((failure: unknown) =>
              say(failure instanceof Error ? failure.message : 'I could not look just now.'),
            );
          return true;
        }
        case 'partner-shops': {
          // New partner shops waiting for approval, by name, never their products (ruling 86):
          // from the overview where this job has it, otherwise from the shops themselves.
          const overview = mine.includes('overview');
          const shops = mine.includes('partners');
          if (!overview && !shops) {
            say("New shop partners aren't part of your job, so I can't answer that.");
            return true;
          }
          // They are on the Shops and organisations tab, shown on the screen too.
          if (shops) latest.current.onOpen('partners');
          const fromOverview: Promise<string | null> = overview
            ? fetchStaffOverview(key).then((data) =>
                data.work.partnerShopsWaiting === undefined
                  ? null
                  : partnerShopsWords(
                      data.work.partnerShopsWaiting,
                      data.work.partnerShopsWaitingNames ?? [],
                    ),
              )
            : Promise.resolve(null);
          void fromOverview
            .catch(() => null)
            .then(async (words) => {
              if (words) return words;
              if (!shops) throw new Error('Not loaded.');
              const data = await fetchStaffPartners(key);
              const pending = data.shops.filter((shop) => shop.status === 'pending');
              return partnerShopsWords(
                pending.length,
                pending.map((shop) => shop.name),
              );
            })
            .then((words) => say(words))
            .catch(() =>
              say("I couldn't load the new shop partners just now, so I don't know yet."),
            );
          return true;
        }
        case 'rung-back': {
          if (!needs('enquiries')) return true;
          const row = current() as StaffEnquiry;
          confirm(`Mark ${row.organisation} as rung back?`, () =>
            markEnquiryHandled(key, row.id).then(() => 'Marked as rung back.'),
          );
          return true;
        }
      }
    }

    voice.setPageCommands(handle);
    void load().then(() => {
      if (!cancelled) say(opening());
    });
    return () => {
      cancelled = true;
      latest.current.ozi.setPageCommands(null);
    };
    // Once per person signed in: everything else is read through `latest`.
  }, [staffKey]);

  return null;
}
